import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Sparkles, TrendingDown, TrendingUp } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Progress } from "@/components/ui/progress";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

interface CreditsSummary {
  quota: number;
  used: number;
  remaining: number;
  plan: string;
  next_reset_at?: string;
}

export function CreditsBadge() {
  const [credits, setCredits] = useState<CreditsSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchCredits = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('account-summary');
      
      if (error) {
        console.error('Error fetching credits:', error);
        return;
      }

      setCredits(data);
    } catch (error) {
      console.error('Error fetching credits:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCredits();

    // Subscribe to credits changes with more specific targeting
    const channel = supabase
      .channel("credits-badge-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "credits_ledger",
        },
        (payload) => {
          console.log('[CREDITS-BADGE] Ledger changed:', payload);
          fetchCredits();
        }
      )
      .subscribe();

    // Listen for manual credit update events
    const handleCreditsUpdate = () => {
      console.log('[CREDITS-BADGE] Manual update event received');
      fetchCredits();
    };
    window.addEventListener('creditsUpdated', handleCreditsUpdate);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener('creditsUpdated', handleCreditsUpdate);
    };
  }, []);

  if (loading) {
    return (
      <div className="p-3 bg-muted/30 rounded-lg border border-border/50">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Loading credits...</span>
        </div>
      </div>
    );
  }

  if (!credits) {
    return null;
  }

  const resetText = credits.next_reset_at 
    ? `Resets ${formatDistanceToNow(new Date(credits.next_reset_at), { addSuffix: true })}`
    : 'Next billing cycle';

  const usagePercent = (credits.used / credits.quota) * 100;
  const isLow = credits.remaining <= 10;
  const isVeryLow = credits.remaining <= 5;

  return (
    <HoverCard openDelay={200}>
      <HoverCardTrigger asChild>
        <div className="p-3 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent rounded-lg border border-primary/20 cursor-pointer hover:border-primary/40 transition-all">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-xs font-medium text-muted-foreground">Credits</span>
            </div>
            {isVeryLow ? (
              <TrendingDown className="h-3 w-3 text-destructive" />
            ) : isLow ? (
              <TrendingDown className="h-3 w-3 text-orange-500" />
            ) : (
              <TrendingUp className="h-3 w-3 text-green-500" />
            )}
          </div>
          
          <div className="flex items-baseline gap-1 mb-2">
            <span className={`text-2xl font-bold ${isVeryLow ? 'text-destructive' : isLow ? 'text-orange-500' : 'text-primary'}`}>
              {credits.remaining}
            </span>
            <span className="text-xs text-muted-foreground">/ {credits.quota}</span>
          </div>

          <div className={`${isVeryLow ? '[&>div]:bg-destructive' : isLow ? '[&>div]:bg-orange-500' : '[&>div]:bg-primary'}`}>
            <Progress 
              value={100 - usagePercent} 
              className="h-1.5 bg-muted"
            />
          </div>
          
          <div className="mt-2 text-xs text-muted-foreground">
            {credits.used} used • {resetText.replace('Resets ', '')}
          </div>
        </div>
      </HoverCardTrigger>
      
      <HoverCardContent side="right" className="w-72">
        <div className="space-y-3">
          <div>
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              Credit Balance
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Quota:</span>
                <span className="font-bold">{credits.quota}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Used:</span>
                <span className="font-medium">{credits.used}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-muted-foreground">Available:</span>
                <span className={`font-bold ${isVeryLow ? 'text-destructive' : isLow ? 'text-orange-500' : 'text-primary'}`}>
                  {credits.remaining}
                </span>
              </div>
            </div>
          </div>

          <div className="pt-2 border-t">
            <p className="text-xs font-semibold text-muted-foreground mb-2">Cost per Action</p>
            <div className="grid grid-cols-3 gap-1 text-xs">
              <div className="p-1.5 bg-muted/50 rounded text-center">
                <div className="font-bold">1</div>
                <div className="text-muted-foreground text-[10px]">Resume</div>
              </div>
              <div className="p-1.5 bg-muted/50 rounded text-center">
                <div className="font-bold">2</div>
                <div className="text-muted-foreground text-[10px]">Pro</div>
              </div>
              <div className="p-1.5 bg-muted/50 rounded text-center">
                <div className="font-bold">5</div>
                <div className="text-muted-foreground text-[10px]">Premium</div>
              </div>
            </div>
            {credits.plan === 'premium' && (
              <div className="mt-2 p-2 bg-primary/10 border border-primary/20 rounded text-xs">
                <p className="text-primary font-semibold">✨ Unlimited 1-2 credit actions</p>
              </div>
            )}
          </div>

          {credits.next_reset_at && (
            <div className="pt-2 border-t text-xs text-muted-foreground">
              {resetText}
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
